import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getSupabaseAdmin } from "@/integrations/external-supabase/client.server";
import {
  urlPorAlias,
  WILDCARD,
  type AliasDestino,
  type AliasResolver,
} from "@/lib/portal-alias";

/**
 * Resolve the 5 portal URL segments to their canonical BD display names,
 * plus the top-N listings for the current facet (for JSON-LD ItemList and
 * for zero-results detection).
 *
 * URL schema (as understood by the current data model):
 *   /portal/:operacion/:uso/:provincia/:ciudad/:barrio
 *
 * The SEO spec calls slot 2 "tipo" and slot 5 "distrito"; internally they are
 * `usos_local` and `barrios` — we surface the friendly names verbatim.
 */
export const getPortalSeoData = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z
      .object({
        segments: z.array(z.string()).max(5).default([]),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const [opSlug, tipoSlug, provSlug, ciudadSlug, distritoSlug] =
      data.segments.map((s) => s.toLowerCase());

    const skip = (s: string | undefined) =>
      !s || s === "todas" ? null : s;

    type Row = { id: string; slug: string | null; nombre: string };
    // NO usar `.maybeSingle()`: un mismo slug puede existir en varias filas
    // (p. ej. "leganes" cuelga de las provincias `madrid` y `zona-sur-madrid`;
    // ~600 slugs de ciudades y ~200 de barrios están duplicados). Con
    // `.maybeSingle()` eso devuelve error → data null → 404 falso.
    const bySlug = async (
      table: "operaciones" | "usos_local" | "provincias" | "ciudades" | "barrios",
      slug: string | null,
    ): Promise<Row[]> => {
      if (!slug) return [];
      const { data: rows, error } = await sb
        .from(table)
        .select("id, slug, nombre")
        .eq("slug", slug)
        .order("id")
        .limit(100);
      if (error) throw new Error(error.message);
      return (rows ?? []) as Row[];
    };

    const [opRows, tipoRows, provRows, ciudadRows, distritoRows] =
      await Promise.all([
        bySlug("operaciones", skip(opSlug)),
        bySlug("usos_local", skip(tipoSlug)),
        bySlug("provincias", skip(provSlug)),
        bySlug("ciudades", skip(ciudadSlug)),
        bySlug("barrios", skip(distritoSlug)),
      ]);
    const ids = (rows: Row[]) => rows.map((r) => r.id);

    // Best-effort listing count + top items for ItemList JSON-LD.
    // Small query, no join with media (we already have url/id).
    let items: Array<{ id: string; title: string }> = [];
    let total = 0;
    try {
      // Sin `alias`: es un nombre interno y `anon` no tiene SELECT sobre esa
      // columna, así que pedirla hacía fallar la consulta entera (42501) y la
      // página salía sin recuento ni ItemList.
      let q = sb
        .from("anuncios")
        .select("id, referencia, tipo_local_id, ciudad_id, operacion_id", {
          count: "exact",
        })
        .eq("estado", "publicado")
        .eq("visibilidad", "publico")
        .limit(10);

      // Slugs duplicados → filtramos por todos sus ids. Las facetas se
      // combinan con AND, así que provincia + ciudad sigue siendo exacto.
      if (opRows.length) q = q.in("operacion_id", ids(opRows));
      if (provRows.length) q = q.in("provincia_id", ids(provRows));
      if (ciudadRows.length) q = q.in("ciudad_id", ids(ciudadRows));
      if (distritoRows.length) q = q.in("barrio_id", ids(distritoRows));
      // Note: tipo (uso_local) is a many-to-many via anuncio_usos_local; we
      // skip that filter for the count — the difference is small and this data
      // is only used for SEO signals, not for the visible grid.

      const { data: rows, count, error } = await q;
      if (!error) {
        total = count ?? 0;
        items = (rows ?? []).map((r: any) => ({
          id: r.id as string,
          title: (r.referencia as string | null) || "Anuncio",
        }));
      }
    } catch {
      // Non-fatal.
    }

    return {
      op: opRows[0] ?? null,
      tipo: tipoRows[0] ?? null,
      provincia: provRows[0] ?? null,
      ciudad: ciudadRows[0] ?? null,
      distrito: distritoRows[0] ?? null,
      total,
      items,
    };
  });

/**
 * URL nueva para una URL del portal con ubicaciones fusionadas (slugs que
 * ahora son alias), o `null` si no hay nada que redirigir. Ver
 * `src/lib/portal-alias.ts`.
 */
export const resolverAliasPortal = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z
      .object({
        basePath: z.enum(["/portal", "/portal-vip"]),
        segments: z.array(z.string().max(200)).max(5).default([]),
        search: z.record(z.string(), z.string().max(2000)).default({}),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const cache = new Map<string, AliasDestino | null>();
    const resolve: AliasResolver = async (nivel, slug, padre) => {
      const key = `${nivel}|${slug}|${padre ?? ""}`;
      if (cache.has(key)) return cache.get(key) ?? null;
      const { data: r, error } = await sb.rpc("geo_slug_principal", {
        _nivel: nivel,
        _slug: slug,
        _padre_slug: padre ?? undefined,
      });
      const destino = error || !r ? null : (r as AliasDestino);
      cache.set(key, destino);
      return destino;
    };
    const url = await urlPorAlias(data.segments, data.search, resolve);
    if (!url) return null;
    const segs = [...url.segments];
    while (segs.length && segs[segs.length - 1] === WILDCARD) segs.pop();
    return {
      pathname: segs.length ? `${data.basePath}/${segs.join("/")}` : data.basePath,
      search: url.search,
    };
  });
